import { apiService } from './api'
import { z } from 'zod'
import type { ScanFormData } from '../validation/schemas'
import { SCAN_ENDPOINTS } from '../../api/endpoints'

const TimeFrameSchema = z.object({
  preset: z.string(),
  from: z
    .string()
    .nullable()
    .optional()
    .transform(val => (val ? new Date(val) : undefined)),
  to: z
    .string()
    .nullable()
    .optional()
    .transform(val => (val ? new Date(val) : undefined)),
})

const UserSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  displayName: z.string().nullable(),
  email: z.string(),
})

const ScanResponseSchema = z.object({
  data: z.object({
    id: z.string(),
  }),
})

const ScanListItemSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  status: z.string(),
  progress: z.number().nullable().optional(),
  location: z.string(),
  keywords: z.array(z.string()),
  searchQuery: z.string().nullable().optional(),
  timeFrame: TimeFrameSchema,
  createdAt: z.string(),
  crisisId: z.string().optional(),
  createdBy: UserSchema.optional(),
})

const ScanListResponseSchema = z.object({
  data: z.array(ScanListItemSchema),
})

const GenerateAnalysisResponseSchema = z.object({
  data: z.object({
    generatedText: z.string(),
    scanId: z.string(),
  }),
})

const GenerateBlockContentResponseSchema = z.object({
  data: z.object({
    generatedContent: z.string(),
    scanId: z.string(),
    blockType: z.string(),
  }),
})

// Schema for full scan details
const ScanSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  keywords: z.array(z.string()),
  searchQuery: z.string().nullable().optional(),
  location: z.string(),
  timeFrame: TimeFrameSchema,
  createdBy: UserSchema.optional(),
  confirmedAt: z
    .string()
    .optional()
    .transform(val => (val ? new Date(val) : undefined)),
  confirmedBy: z.string().optional(),
  totalMessages: z.number().nullable(),
  blocks: z.array(
    z.object({
      id: z.string(),
      title: z.string(),
      content: z.string(),
      isGenerated: z.boolean(),
    })
  ),
  sources: z.array(z.any()).optional().or(z.record(z.string(), z.any()).optional()),
  originalAiAnswers: z.record(z.string(), z.string()).optional().nullable(),
})

const ScanDetailResponseSchema = z.object({
  data: ScanSchema,
})

export type ScanResponse = z.infer<typeof ScanResponseSchema>
export type ScanListItem = z.infer<typeof ScanListItemSchema>
export type ScanListResponse = z.infer<typeof ScanListResponseSchema>
export type GenerateAnalysisResponse = z.infer<typeof GenerateAnalysisResponseSchema>
export type GenerateBlockContentResponse = z.infer<typeof GenerateBlockContentResponseSchema>
export type ScanDetailResponse = z.infer<typeof ScanDetailResponseSchema>
const ScanQueryPreviewSchema = z.object({
  data: z.object({
    query: z.string(),
    total: z.number(),
    messages: z.array(
      z.object({
        title: z.string().nullable().optional(),
        text: z.string(),
        sourceType: z.string().nullable().optional(),
        author: z.union([z.string(), z.record(z.string(), z.unknown())]).nullable().optional(),
        publishedAt: z.union([z.string(), z.number()]).nullable().optional(),
        url: z.string().nullable().optional(),
      })
    ),
  }),
})

export type ScanQueryPreview = z.infer<typeof ScanQueryPreviewSchema>

export class ScanService {
  async deleteScan(scanId: string): Promise<{ data: { id: string; deleted: boolean } }> {
    const schema = z.object({
      data: z.object({
        id: z.string(),
        deleted: z.boolean(),
      }),
    })

    return apiService.delete(SCAN_ENDPOINTS.DELETE(scanId), schema)
  }
  async createScan(data: ScanFormData): Promise<ScanResponse> {
    return apiService.post(SCAN_ENDPOINTS.NEW, data, undefined, ScanResponseSchema)
  }

  async retryScan(scanId: string): Promise<{ data: { id: string; status: string } }> {
    const schema = z.object({
      data: z.object({ id: z.string(), status: z.string() }),
    })
    return apiService.post(SCAN_ENDPOINTS.RETRY(scanId), {}, undefined, schema)
  }

  async previewQuery(data: ScanFormData): Promise<ScanQueryPreview> {
    return apiService.post(SCAN_ENDPOINTS.PREVIEW, data, undefined, ScanQueryPreviewSchema)
  }

  async getScans(crisisId: string, includeDeleted: boolean = false): Promise<ScanListResponse> {
    const params: Record<string, string> = {}
    if (crisisId) {
      params.crisis_id = crisisId
    }
    if (includeDeleted) {
      params.include_deleted = 'true'
    }
    const queryString = new URLSearchParams(params).toString()
    const url = queryString ? `${SCAN_ENDPOINTS.LIST}?${queryString}` : SCAN_ENDPOINTS.LIST

    return apiService.get(url, ScanListResponseSchema)
  }

  async getScan(scanId: string): Promise<ScanDetailResponse> {
    return apiService.get(SCAN_ENDPOINTS.SCAN_BY_ID(scanId), ScanDetailResponseSchema)
  }

  async generateAnalysis(scanId: string): Promise<GenerateAnalysisResponse> {
    return apiService.post(
      SCAN_ENDPOINTS.GENERATE_ANALYSIS(scanId),
      {},
      undefined,
      GenerateAnalysisResponseSchema
    )
  }

  async generateBlockContent(
    scanId: string,
    blockType: string,
    blockTitle: string
  ): Promise<GenerateBlockContentResponse> {
    return apiService.post(
      SCAN_ENDPOINTS.GENERATE_BLOCK(scanId),
      {
        blockType: blockType,
        blockTitle: blockTitle,
      },
      undefined,
      GenerateBlockContentResponseSchema
    )
  }

  async updateBlockContent(
    scanId: string,
    blockId: string,
    content: string
  ): Promise<{ data: { updated: boolean } }> {
    const schema = z.object({
      data: z.object({
        scanId: z.string(),
        blockId: z.string(),
        updated: z.boolean(),
      }),
    })

    return apiService.post(
      SCAN_ENDPOINTS.UPDATE_BLOCK(scanId),
      {
        blockId: blockId,
        content: content,
      },
      undefined,
      schema
    )
  }

  async confirmScan(scanId: string): Promise<{ data: { id: string; status: string } }> {
    const schema = z.object({
      data: z.object({
        id: z.string(),
        status: z.string(),
      }),
    })

    return apiService.post(SCAN_ENDPOINTS.CONFIRM(scanId), {}, undefined, schema)
  }
}

export const scanService = new ScanService()
